import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  CheckCircle2,
  Users,
  Building2,
  FileSignature,
  Cpu,
  Mail,
  ShoppingCart,
  Store,
  Trash2,
  RefreshCw,
  AlertCircle,
  ArrowRight,
  Phone,
  CalendarCheck,
  CalendarClock,
  BadgeCheck,
  XCircle,
  CreditCard,
  ArrowLeftRight,
  LucideIcon,
} from 'lucide-react'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'
import api from '../services/api'
import type { Plan, CurrentSubscription } from '../types'

// ── Types ─────────────────────────────────────────────────────────────────────

interface CartEntry {
  plan: Plan
  quantity: number
}

interface ProratedPaymentStepData {
  clientSecret: string
  stripePromise: ReturnType<typeof loadStripe>
  amountHT: number
  vatRate: number
  amountTTC: number
  proratedCreditHT: number
  paymentIntentId: string
  planId: number
  quantity: number
  proration: {
    current_plan_name: string
    new_plan_name: string
    new_price_ht: number
    period_start: string
    next_billing_date: string
    total_period_days: number
    remaining_days: number
    prorated_credit_ht: number
    amount_due_ht: number
    vat_rate: number
    amount_due_ttc: number
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt(n: number): string {
  return n.toLocaleString('fr-FR')
}

function fmtPrice(n: number): string {
  return Number(n).toFixed(2).replace('.', ',')
}

function fmtDate(d: string, locale = 'fr-FR'): string {
  return new Date(d).toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' })
}

type PlanQuotaKey =
  | 'max_users'
  | 'max_clients'
  | 'max_signatures'
  | 'max_ai_tokens'
  | 'max_emails'
  | 'max_sku_free'
// Each item carries a translation key suffix for i18n label lookup
const QUOTA_ITEMS: { icon: LucideIcon; key: PlanQuotaKey; labelKey: string }[] = [
  { icon: Users, key: 'max_users', labelKey: 'tarifs.quota.users' },
  { icon: Building2, key: 'max_clients', labelKey: 'tarifs.quota.clients' },
  { icon: FileSignature, key: 'max_signatures', labelKey: 'tarifs.quota.signatures' },
  { icon: Cpu, key: 'max_ai_tokens', labelKey: 'tarifs.quota.aiTokens' },
  { icon: Mail, key: 'max_emails', labelKey: 'tarifs.quota.emails' },
  { icon: Store, key: 'max_sku_free', labelKey: 'tarifs.quota.catalog' },
]

interface SubscribeApiResponse {
  success?: boolean
  message?: string
  requires_payment?: boolean
  client_secret?: string
  publishable_key?: string
  amount_ht?: number
  vat_rate?: number
  amount_ttc?: number
  payment_intent_id?: string
  proration?: ProratedPaymentStepData['proration']
  scheduled?: boolean
  effective_date?: string | null
}

// ── Plan card ─────────────────────────────────────────────────────────────────

interface PlanCardProps {
  plan: Plan
  currentPlan: string | null
  selected: boolean
  onSelect: () => void
}

function PlanCard({ plan, currentPlan, selected, onSelect }: PlanCardProps) {
  const { t, i18n } = useTranslation()
  const desc =
    (i18n.language.startsWith('en') ? plan.description_en : plan.description_fr) ||
    plan.description_fr ||
    plan.description_en ||
    plan.description ||
    ''
  const isCurrent = currentPlan === plan.name

  if (plan.is_custom) {
    return (
      <div
        className={`relative flex flex-col rounded-2xl border-2 p-6 transition-all
        ${selected ? 'border-purple-600 shadow-lg shadow-purple-100 dark:shadow-purple-900/30' : 'border-gray-200 dark:border-gray-700'}
        bg-gradient-to-br from-gray-900 to-gray-800 text-white`}
      >
        {isCurrent && (
          <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full text-xs font-semibold bg-green-500 text-white shadow">
            {t('tarifs.planCard.currentPlan')}
          </span>
        )}
        <div className="mb-4">
          <h3 className="text-xl font-bold text-white">{plan.name}</h3>
          <p className="text-sm text-gray-300 mt-1">{desc}</p>
        </div>
        <div className="text-3xl font-extrabold text-white mb-1">
          {t('tarifs.planCard.onQuote')}
        </div>
        <p className="text-sm text-gray-400 mb-6">{t('tarifs.planCard.customQuotas')}</p>
        <div className="space-y-2 mb-6 flex-1">
          {QUOTA_ITEMS.map(({ icon: Icon, key, labelKey }) => (
            <div key={key} className="flex items-center gap-2 text-sm text-gray-300">
              <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span>
                {t(labelKey)} :{' '}
                <strong className="text-white">{t('tarifs.planCard.customValue')}</strong>
              </span>
            </div>
          ))}
        </div>
        <a
          href="mailto:contact@iseffi.fr"
          className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl font-semibold text-sm
            bg-white text-gray-900 hover:bg-gray-100 transition-colors"
        >
          <Phone className="w-4 h-4" /> {t('tarifs.planCard.contactUs')}
        </a>
      </div>
    )
  }

  return (
    <div
      onClick={onSelect}
      className={`relative flex flex-col rounded-2xl border-2 p-6 cursor-pointer transition-all
        ${
          selected
            ? 'border-purple-600 shadow-lg shadow-purple-100 dark:shadow-purple-900/30 bg-purple-50 dark:bg-purple-900/10'
            : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-purple-300 dark:hover:border-purple-700'
        }
      `}
    >
      {isCurrent && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full text-xs font-semibold bg-green-500 text-white shadow">
          {t('tarifs.planCard.currentPlan')}
        </span>
      )}
      {selected && <CheckCircle2 className="absolute top-4 right-4 w-5 h-5 text-purple-600" />}

      <div className="mb-4">
        <h3 className="text-xl font-bold text-gray-900 dark:text-white">{plan.name}</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{desc}</p>
      </div>

      <div className="flex items-baseline gap-1 mb-1">
        <span className="text-3xl font-extrabold text-gray-900 dark:text-white">
          {fmtPrice(plan.base_price)} €
        </span>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {t('tarifs.planCard.perPackPerMonth')}
        </span>
      </div>
      <p className="text-xs text-gray-400 mb-6">{t('tarifs.planCard.billingMonthly')}</p>

      <div className="space-y-2 mb-6 flex-1">
        {QUOTA_ITEMS.map(({ icon: Icon, key, labelKey }) => (
          <div
            key={key}
            className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300"
          >
            <Icon className="w-4 h-4 text-purple-400 flex-shrink-0" />
            <span>
              {t(labelKey)} : <strong>{fmt(plan[key])}</strong>
            </span>
          </div>
        ))}
      </div>

      <button
        onClick={(e) => {
          e.stopPropagation()
          onSelect()
        }}
        className={`flex items-center justify-center gap-2 w-full py-2.5 rounded-xl font-semibold text-sm transition-colors
          ${
            selected
              ? 'bg-purple-600 text-white hover:bg-purple-700'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-white hover:bg-purple-100 dark:hover:bg-purple-900/30'
          }`}
      >
        {selected ? (
          <>
            <CheckCircle2 className="w-4 h-4" /> {t('tarifs.planCard.selected')}
          </>
        ) : (
          t('tarifs.planCard.choosePlan')
        )}
      </button>
    </div>
  )
}

// ── Prorated payment form (Stripe Elements inner) ─────────────────────────────

interface ProratedPaymentFormProps {
  step: ProratedPaymentStepData
  onSuccess: () => void
  onCancel: () => void
}

function ProratedPaymentFormInner({ step, onSuccess, onCancel }: ProratedPaymentFormProps) {
  const { t, i18n } = useTranslation()
  const stripe = useStripe()
  const elements = useElements()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [unrecoverable, setUnrecoverable] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!stripe || !elements) return
    setLoading(true)
    setError('')
    setUnrecoverable(false)

    const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
      confirmParams: { return_url: window.location.href },
    })

    if (stripeError) {
      setError(stripeError.message ?? t('tarifs.proration.stripeError'))
      const unrecoverableCodes = [
        'payment_intent_unexpected_state',
        'payment_intent_incompatible_payment_method',
        'payment_method_not_available',
      ]
      if (stripeError.code && unrecoverableCodes.includes(stripeError.code)) {
        setUnrecoverable(true)
      }
      setLoading(false)
      return
    }

    try {
      await api.confirmSubscriptionChange(
        paymentIntent?.id ?? step.paymentIntentId,
        step.planId,
        step.quantity
      )
      onSuccess()
    } catch {
      setError(t('tarifs.proration.activationError'))
    }
    setLoading(false)
  }

  const p = step.proration

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      {/* Proration breakdown */}
      <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-lg p-3 text-xs space-y-1">
        <p className="font-semibold text-purple-800 dark:text-purple-300 flex items-center gap-1.5">
          <ArrowLeftRight className="w-3.5 h-3.5" /> {t('tarifs.proration.planChange')}
        </p>
        <div className="flex justify-between text-gray-600 dark:text-gray-400">
          <span>{t('tarifs.proration.newPlan', { name: p.new_plan_name })}</span>
          <span>{fmtPrice(p.new_price_ht)} € HT</span>
        </div>
        <div className="flex justify-between text-green-700 dark:text-green-400">
          <span>
            {t('tarifs.proration.proratedCredit', {
              remaining: p.remaining_days,
              total: p.total_period_days,
            })}
          </span>
          <span>− {fmtPrice(p.prorated_credit_ht)} € HT</span>
        </div>
        <div className="border-t border-purple-200 dark:border-purple-700 pt-1 mt-1 space-y-0.5">
          <div className="flex justify-between text-gray-600 dark:text-gray-400">
            <span>{t('tarifs.proration.amountDueHT')}</span>
            <span>{fmtPrice(step.amountHT)} €</span>
          </div>
          <div className="flex justify-between text-gray-600 dark:text-gray-400">
            <span>{t('tarifs.proration.vat', { rate: step.vatRate })}</span>
            <span>{fmtPrice(step.amountTTC - step.amountHT)} €</span>
          </div>
          <div className="flex justify-between font-bold text-purple-900 dark:text-purple-100 text-sm">
            <span>{t('tarifs.proration.totalTTC')}</span>
            <span>{fmtPrice(step.amountTTC)} €</span>
          </div>
        </div>
        <p className="text-gray-500 dark:text-gray-500 pt-0.5">
          {t('tarifs.proration.fromDate', {
            date: fmtDate(p.next_billing_date, i18n.language),
            price: fmtPrice(p.new_price_ht),
          })}
        </p>
      </div>

      <PaymentElement />

      {error && (
        <div className="space-y-1">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          {unrecoverable && (
            <button
              type="button"
              onClick={onCancel}
              className="text-sm text-blue-600 dark:text-blue-400 underline"
            >
              {t('tarifs.proration.restart')}
            </button>
          )}
        </div>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="flex-1 py-2.5 rounded-xl font-semibold text-sm border border-gray-300 dark:border-gray-600
            text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
        >
          {t('common.cancel')}
        </button>
        <button
          type="submit"
          disabled={loading || !stripe}
          className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl font-semibold text-sm
            bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin" /> {t('tarifs.proration.paying')}
            </>
          ) : (
            <>
              <CreditCard className="w-4 h-4" />{' '}
              {t('tarifs.proration.pay', { amount: fmtPrice(step.amountTTC) })}
            </>
          )}
        </button>
      </div>
    </form>
  )
}

// ── Cart panel ────────────────────────────────────────────────────────────────

interface CartPanelProps {
  entry: CartEntry | null
  onQtyChange: (qty: number) => void
  onRemove: () => void
  onOrder: () => void
  ordering: boolean
  success: string | null
  error: string | null
  paymentStep?: ProratedPaymentStepData | null
  onPaymentSuccess?: () => void
  onCancelPayment?: () => void
}

function CartPanel({
  entry,
  onQtyChange,
  onRemove,
  onOrder,
  ordering,
  success,
  error,
  paymentStep,
  onPaymentSuccess,
  onCancelPayment,
}: CartPanelProps) {
  const { t } = useTranslation()

  if (!entry) {
    return (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-6 text-center">
        <ShoppingCart className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
        <p className="text-sm text-gray-500 dark:text-gray-400">{t('tarifs.cart.empty')}</p>
      </div>
    )
  }

  const { plan, quantity } = entry
  const total = Number(plan.base_price) * quantity

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-6 space-y-4 sticky top-4">
      <div className="flex items-center gap-2">
        <ShoppingCart className="w-5 h-5 text-purple-600" />
        <h3 className="font-semibold text-gray-900 dark:text-white">
          {t('tarifs.cart.yourOrder')}
        </h3>
      </div>

      {/* Selected plan summary */}
      <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="font-semibold text-gray-900 dark:text-white">{plan.name}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {fmtPrice(plan.base_price)} € {t('tarifs.planCard.perPackPerMonth')}
            </p>
          </div>
          {!paymentStep && (
            <button
              onClick={onRemove}
              className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Quantity selector — hidden during payment step */}
        {!paymentStep && (
          <div className="mt-3 flex items-center justify-between">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {t('tarifs.cart.packCount')}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => onQtyChange(Math.max(1, quantity - 1))}
                className="w-7 h-7 rounded-full border border-gray-300 dark:border-gray-600 flex items-center justify-center text-sm
                  hover:border-purple-400 hover:text-purple-600 transition-colors dark:text-white"
              >
                −
              </button>
              <span className="w-8 text-center font-semibold text-gray-900 dark:text-white">
                {quantity}
              </span>
              <button
                onClick={() => onQtyChange(quantity + 1)}
                className="w-7 h-7 rounded-full border border-gray-300 dark:border-gray-600 flex items-center justify-center text-sm
                  hover:border-purple-400 hover:text-purple-600 transition-colors dark:text-white"
              >
                +
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Effective quotas preview — hidden during payment step */}
      {!paymentStep && (
        <div>
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
            {t('tarifs.cart.effectiveQuotas')}
          </p>
          <div className="space-y-1.5">
            {QUOTA_ITEMS.map(({ icon: Icon, key, labelKey }) => {
              const effective = plan[key] * quantity
              return (
                <div key={key} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                    <Icon className="w-3.5 h-3.5" />
                    <span>{t(labelKey)}</span>
                  </div>
                  <strong className="text-gray-900 dark:text-white">{fmt(effective)}</strong>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Total — HT / TVA / TTC breakdown — hidden during payment step (shown in form instead) */}
      {!paymentStep && (
        <div className="border-t border-gray-200 dark:border-gray-700 pt-3 space-y-1">
          <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
            <span>{t('tarifs.cart.totalHT')}</span>
            <span>{total.toFixed(2).replace('.', ',')} €</span>
          </div>
          <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
            <span>{t('tarifs.cart.vat')}</span>
            <span>{(total * 0.2).toFixed(2).replace('.', ',')} €</span>
          </div>
          <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-1 mt-1">
            <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">
              {t('tarifs.cart.totalTTC')}
            </span>
            <span className="text-lg font-bold text-purple-600">
              {(total * 1.2).toFixed(2).replace('.', ',')} €
            </span>
          </div>
        </div>
      )}

      {/* Stripe payment step for plan change proration */}
      {paymentStep && (
        <Elements
          stripe={paymentStep.stripePromise}
          options={{ clientSecret: paymentStep.clientSecret, appearance: { theme: 'stripe' } }}
        >
          <ProratedPaymentFormInner
            step={paymentStep}
            onSuccess={onPaymentSuccess ?? (() => {})}
            onCancel={onCancelPayment ?? (() => {})}
          />
        </Elements>
      )}

      {/* Messages */}
      {success && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm">
          <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{success}</span>
        </div>
      )}
      {error && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {/* Commander button — hidden during payment step */}
      {!paymentStep && (
        <>
          <button
            onClick={onOrder}
            disabled={ordering}
            className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-semibold text-sm
              bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {ordering ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" /> {t('tarifs.cart.processing')}
              </>
            ) : (
              <>
                <ArrowRight className="w-4 h-4" /> {t('tarifs.cart.order')}
              </>
            )}
          </button>
          <p className="text-xs text-gray-400 dark:text-gray-500 text-center">
            {t('tarifs.cart.billingInfo')}
          </p>
        </>
      )}
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function Tarifs() {
  const { t, i18n } = useTranslation()
  const [plans, setPlans] = useState<Plan[]>([])
  const [loading, setLoading] = useState(true)
  const [currentSub, setCurrentSub] = useState<CurrentSubscription | null>(null)

  const [cartEntry, setCartEntry] = useState<CartEntry | null>(null)
  const [ordering, setOrdering] = useState(false)
  const [orderMsg, setOrderMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  // Prorated payment step state (active subscription plan change)
  const [paymentStep, setPaymentStep] = useState<ProratedPaymentStepData | null>(null)

  // Quantity change on current subscription
  const [editingQty, setEditingQty] = useState(false)
  const [editQtyValue, setEditQtyValue] = useState(1)
  const [editQtyBusy, setEditQtyBusy] = useState(false)
  const [editQtyMsg, setEditQtyMsg] = useState<{
    type: 'success' | 'error' | 'info'
    text: string
  } | null>(null)
  const [editPayStep, setEditPayStep] = useState<ProratedPaymentStepData | null>(null)

  // Load plans and current subscription
  useEffect(() => {
    const loadData = async () => {
      setLoading(true)
      try {
        const [plansRes, subRes] = await Promise.allSettled([
          api.getPublicPlans(),
          api.getMySubscription(),
        ])

        if (plansRes.status === 'fulfilled' && plansRes.value.success) {
          setPlans(plansRes.value.plans ?? [])
        }
        if (subRes.status === 'fulfilled' && subRes.value.success) {
          setCurrentSub(subRes.value.subscription ?? null)
        }
      } catch {
        // silently — plans still shown
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [])

  const handleSelectPlan = (plan: Plan) => {
    setOrderMsg(null)
    setPaymentStep(null)
    if (cartEntry?.plan.id === plan.id) {
      setCartEntry(null) // deselect
    } else {
      setCartEntry({ plan, quantity: 1 })
    }
  }

  const handleOrder = async () => {
    if (!cartEntry) return
    setOrdering(true)
    setOrderMsg(null)
    setPaymentStep(null)
    try {
      const r = (await api.subscribeToPlan(
        cartEntry.plan.id,
        cartEntry.quantity
      )) as SubscribeApiResponse

      if (r.requires_payment && r.client_secret && r.publishable_key) {
        // Mid-period plan upgrade: prorated amount required
        const stripePromise = loadStripe(r.publishable_key)
        setPaymentStep({
          clientSecret: r.client_secret,
          stripePromise,
          amountHT: r.proration?.amount_due_ht ?? 0,
          vatRate: r.proration?.vat_rate ?? 20,
          amountTTC: r.proration?.amount_due_ttc ?? 0,
          proratedCreditHT: r.proration?.prorated_credit_ht ?? 0,
          paymentIntentId: r.payment_intent_id ?? '',
          planId: cartEntry.plan.id,
          quantity: cartEntry.quantity,
          proration: r.proration!,
        })
      } else if (r.scheduled) {
        // Downgrade: scheduled at next billing date — no immediate payment
        const effectiveDate = r.effective_date ?? null
        const dateStr = effectiveDate
          ? new Date(effectiveDate).toLocaleDateString(i18n.language, {
              day: '2-digit',
              month: 'long',
              year: 'numeric',
            })
          : t('tarifs.msg.nextBillingDate')
        setOrderMsg({
          type: 'success',
          text: t('tarifs.msg.scheduleDowngrade', { name: cartEntry.plan.name, date: dateStr }),
        })
        const subRes = await api.getMySubscription().catch(() => null)
        if (subRes?.success) setCurrentSub(subRes.subscription ?? null)
        setCartEntry(null)
      } else if (r.success) {
        setOrderMsg({
          type: 'success',
          text: t('tarifs.msg.subscriptionActivated', { name: cartEntry.plan.name }),
        })
        // Refresh current subscription after a short delay
        setTimeout(async () => {
          const subRes = await api.getMySubscription().catch(() => null)
          if (subRes?.success) setCurrentSub(subRes.subscription ?? null)
          setCartEntry(null)
          setOrderMsg(null)
        }, 2500)
      } else {
        setOrderMsg({ type: 'error', text: r.message ?? t('tarifs.msg.subscriptionError') })
      }
    } catch {
      setOrderMsg({ type: 'error', text: t('tarifs.msg.connectionError') })
    } finally {
      setOrdering(false)
    }
  }

  const handlePaymentSuccess = async () => {
    setPaymentStep(null)
    setOrderMsg({
      type: 'success',
      text: t('tarifs.msg.planActivated', { name: cartEntry?.plan.name ?? '' }),
    })
    const subRes = await api.getMySubscription().catch(() => null)
    if (subRes?.success) setCurrentSub(subRes.subscription ?? null)
    setTimeout(() => {
      setCartEntry(null)
      setOrderMsg(null)
    }, 2500)
  }

  const handleCancelPayment = () => {
    setPaymentStep(null)
    setOrderMsg(null)
  }

  const handleOpenEditQty = () => {
    setEditQtyValue(currentSub?.quantity ?? 1)
    setEditQtyMsg(null)
    setEditPayStep(null)
    setEditingQty(true)
  }

  const handleConfirmQtyChange = async () => {
    if (!currentSub) return
    setEditQtyBusy(true)
    setEditQtyMsg(null)
    setEditPayStep(null)
    try {
      const r = (await api.subscribeToPlan(
        currentSub.plan_id,
        editQtyValue
      )) as SubscribeApiResponse

      if (r.requires_payment && r.client_secret && r.publishable_key) {
        const stripePromise = loadStripe(r.publishable_key)
        setEditPayStep({
          clientSecret: r.client_secret,
          stripePromise,
          amountHT: r.proration?.amount_due_ht ?? 0,
          vatRate: r.proration?.vat_rate ?? 20,
          amountTTC: r.proration?.amount_due_ttc ?? 0,
          proratedCreditHT: r.proration?.prorated_credit_ht ?? 0,
          paymentIntentId: r.payment_intent_id ?? '',
          planId: currentSub.plan_id,
          quantity: editQtyValue,
          proration: r.proration!,
        })
      } else if (r.scheduled) {
        const dateStr = r.effective_date
          ? new Date(r.effective_date).toLocaleDateString(i18n.language, {
              day: '2-digit',
              month: 'long',
              year: 'numeric',
            })
          : t('tarifs.msg.nextBillingDate')
        setEditQtyMsg({
          type: 'info',
          text: t('tarifs.msg.qtyReduced', { count: editQtyValue, date: dateStr }),
        })
        const subRes = await api.getMySubscription().catch(() => null)
        if (subRes?.success) setCurrentSub(subRes.subscription ?? null)
      } else if (r.success) {
        setEditQtyMsg({
          type: 'success',
          text: t('tarifs.msg.qtyUpdated', { count: editQtyValue }),
        })
        const subRes = await api.getMySubscription().catch(() => null)
        if (subRes?.success) setCurrentSub(subRes.subscription ?? null)
        setTimeout(() => {
          setEditingQty(false)
          setEditQtyMsg(null)
        }, 2000)
      } else {
        setEditQtyMsg({ type: 'error', text: r.message ?? t('tarifs.msg.modifyError') })
      }
    } catch {
      setEditQtyMsg({ type: 'error', text: t('tarifs.msg.connectionError') })
    } finally {
      setEditQtyBusy(false)
    }
  }

  const handleEditPaySuccess = async () => {
    setEditPayStep(null)
    setEditQtyMsg({ type: 'success', text: t('tarifs.msg.qtyUpdated', { count: editQtyValue }) })
    const subRes = await api.getMySubscription().catch(() => null)
    if (subRes?.success) setCurrentSub(subRes.subscription ?? null)
    setTimeout(() => {
      setEditingQty(false)
      setEditQtyMsg(null)
    }, 2500)
  }

  // Determine the most relevant expiry/billing date to display
  const expiryDate = currentSub?.paid_until_date ?? currentSub?.next_billing_date ?? null
  const isActive = currentSub?.status === 'active'

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <CalendarCheck className="w-6 h-6 text-purple-600" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('tarifs.title')}</h1>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400">{t('tarifs.subtitle')}</p>
      </div>

      {/* ── Current subscription card ── */}
      {loading ? (
        <div className="flex justify-center py-8">
          <RefreshCw className="w-7 h-7 text-gray-400 animate-spin" />
        </div>
      ) : currentSub ? (
        <div
          className={`rounded-2xl border-2 p-6
          ${
            isActive
              ? 'bg-gradient-to-br from-purple-50 to-white dark:from-purple-900/20 dark:to-gray-800 border-purple-200 dark:border-purple-700'
              : 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700'
          }`}
        >
          {/* Top row: plan name + status badge */}
          <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
            <div className="flex items-center gap-3">
              <div
                className={`p-2 rounded-xl ${isActive ? 'bg-purple-100 dark:bg-purple-900/40' : 'bg-gray-200 dark:bg-gray-700'}`}
              >
                <CreditCard
                  className={`w-5 h-5 ${isActive ? 'text-purple-600 dark:text-purple-400' : 'text-gray-500'}`}
                />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                  {currentSub.plan_name}
                  {!currentSub.is_custom && currentSub.quantity > 1 && (
                    <span className="ml-2 text-base font-normal text-purple-600 dark:text-purple-400">
                      × {t('tarifs.sub.pack', { count: currentSub.quantity })}
                    </span>
                  )}
                </h2>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {currentSub.is_custom ? t('tarifs.sub.customPlan') : t('tarifs.sub.monthly')}
                </p>
              </div>
            </div>
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold
              ${
                isActive
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                  : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
              }`}
            >
              {isActive ? (
                <>
                  <BadgeCheck className="w-3.5 h-3.5" /> {t('tarifs.sub.active')}
                </>
              ) : (
                <>
                  <XCircle className="w-3.5 h-3.5" /> {currentSub.status}
                </>
              )}
            </span>
          </div>

          {/* Key metrics row */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
            {/* Expiry / next billing date */}
            <div
              className={`flex items-center gap-2.5 rounded-xl p-3
              ${isActive ? 'bg-white dark:bg-gray-800 shadow-sm' : 'bg-gray-100 dark:bg-gray-700/50'}`}
            >
              <CalendarClock
                className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-purple-500' : 'text-gray-400'}`}
              />
              <div className="min-w-0">
                <p className="text-xs text-gray-500 dark:text-gray-400 leading-tight">
                  {t('tarifs.sub.dueDate')}
                </p>
                <p
                  className={`text-sm font-semibold truncate
                  ${
                    expiryDate
                      ? isActive
                        ? 'text-gray-900 dark:text-white'
                        : 'text-red-600 dark:text-red-400'
                      : 'text-gray-400'
                  }`}
                >
                  {expiryDate
                    ? new Date(expiryDate).toLocaleDateString(i18n.language, {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric',
                      })
                    : '—'}
                </p>
              </div>
            </div>

            {/* Next billing */}
            {currentSub.next_billing_date && currentSub.next_billing_date !== expiryDate && (
              <div className="flex items-center gap-2.5 bg-white dark:bg-gray-800 rounded-xl p-3 shadow-sm">
                <CalendarCheck className="w-5 h-5 text-blue-500 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs text-gray-500 dark:text-gray-400 leading-tight">
                    {t('tarifs.sub.nextBilling')}
                  </p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                    {new Date(currentSub.next_billing_date).toLocaleDateString(i18n.language, {
                      day: '2-digit',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </p>
                </div>
              </div>
            )}

            {/* Credit balance */}
            <div className="flex items-center gap-2.5 bg-white dark:bg-gray-800 rounded-xl p-3 shadow-sm">
              <CreditCard className="w-5 h-5 text-amber-500 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-xs text-gray-500 dark:text-gray-400 leading-tight">
                  {t('tarifs.sub.creditBalance')}
                </p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                  {(currentSub.credit_balance ?? 0).toLocaleString(i18n.language)}
                </p>
              </div>
            </div>
          </div>

          {/* Effective quotas */}
          {!currentSub.is_custom && (
            <div>
              <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">
                {t('tarifs.sub.effectiveQuotas')}
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                {QUOTA_ITEMS.map(({ icon: Icon, key, labelKey }) => {
                  const val =
                    (currentSub[('effective_' + key) as keyof CurrentSubscription] as number) ?? 0
                  return (
                    <div
                      key={key}
                      className="flex items-center gap-1.5 bg-white dark:bg-gray-800 rounded-lg px-3 py-2 shadow-sm"
                    >
                      <Icon className="w-3.5 h-3.5 text-purple-400 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-xs text-gray-400 leading-none truncate">{t(labelKey)}</p>
                        <p className="text-sm font-bold text-gray-900 dark:text-white">
                          {fmt(val)}
                        </p>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Pack quantity editor */}
          {isActive && !currentSub.is_custom && (
            <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-4">
              {!editingQty ? (
                <button
                  onClick={handleOpenEditQty}
                  className="flex items-center gap-2 text-sm font-medium text-purple-600 dark:text-purple-400
                    hover:text-purple-700 dark:hover:text-purple-300 transition-colors"
                >
                  <ArrowLeftRight className="w-4 h-4" />
                  {t('tarifs.sub.editQty', {
                    packs: t('tarifs.sub.pack', { count: currentSub.quantity }),
                  })}
                </button>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('tarifs.sub.editQtyTitle')}
                  </p>

                  {/* Quantity selector */}
                  {!editPayStep && (
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setEditQtyValue((v) => Math.max(1, v - 1))}
                        disabled={editQtyBusy}
                        className="w-8 h-8 rounded-full border border-gray-300 dark:border-gray-600 flex items-center
                          justify-center text-sm hover:border-purple-400 hover:text-purple-600 transition-colors
                          dark:text-white disabled:opacity-40"
                      >
                        −
                      </button>
                      <span className="w-10 text-center font-bold text-lg text-gray-900 dark:text-white">
                        {editQtyValue}
                      </span>
                      <button
                        onClick={() => setEditQtyValue((v) => v + 1)}
                        disabled={editQtyBusy}
                        className="w-8 h-8 rounded-full border border-gray-300 dark:border-gray-600 flex items-center
                          justify-center text-sm hover:border-purple-400 hover:text-purple-600 transition-colors
                          dark:text-white disabled:opacity-40"
                      >
                        +
                      </button>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {t('tarifs.sub.pack', { count: editQtyValue })}
                      </span>
                    </div>
                  )}

                  {/* Inline Stripe payment for pack increase */}
                  {editPayStep && (
                    <Elements
                      stripe={editPayStep.stripePromise}
                      options={{
                        clientSecret: editPayStep.clientSecret,
                        appearance: { theme: 'stripe' },
                      }}
                    >
                      <ProratedPaymentFormInner
                        step={editPayStep}
                        onSuccess={handleEditPaySuccess}
                        onCancel={() => {
                          setEditPayStep(null)
                          setEditQtyMsg(null)
                        }}
                      />
                    </Elements>
                  )}

                  {/* Message */}
                  {editQtyMsg && (
                    <div
                      className={`flex items-start gap-2 p-2.5 rounded-lg text-sm
                      ${
                        editQtyMsg.type === 'success'
                          ? 'bg-green-50 border border-green-200 text-green-700'
                          : editQtyMsg.type === 'info'
                            ? 'bg-blue-50 border border-blue-200 text-blue-700'
                            : 'bg-red-50 border border-red-200 text-red-700'
                      }`}
                    >
                      {editQtyMsg.type === 'success' ? (
                        <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      ) : (
                        <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      )}
                      <span>{editQtyMsg.text}</span>
                    </div>
                  )}

                  {/* Action buttons */}
                  {!editPayStep && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setEditingQty(false)
                          setEditQtyMsg(null)
                        }}
                        disabled={editQtyBusy}
                        className="flex-1 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm
                          text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700
                          transition-colors disabled:opacity-40"
                      >
                        {t('common.cancel')}
                      </button>
                      <button
                        onClick={handleConfirmQtyChange}
                        disabled={editQtyBusy || editQtyValue === currentSub.quantity}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm
                          font-semibold bg-purple-600 text-white hover:bg-purple-700
                          disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        {editQtyBusy ? (
                          <>
                            <RefreshCw className="w-3.5 h-3.5 animate-spin" />{' '}
                            {t('tarifs.sub.inProgress')}
                          </>
                        ) : (
                          <>
                            <CheckCircle2 className="w-3.5 h-3.5" /> {t('tarifs.sub.confirm')}
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Pending downgrade notice */}
          {currentSub.pending_plan_name && currentSub.pending_effective_date && (
            <div className="flex items-start gap-2.5 mt-3 p-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700">
              <ArrowLeftRight className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800 dark:text-amber-300">
                <strong>{t('tarifs.sub.pendingChangeTitle')}</strong>{' '}
                {t('tarifs.sub.pendingChangeBody', {
                  name: currentSub.pending_plan_name,
                  packs:
                    (currentSub.pending_quantity ?? 1) > 1
                      ? t('tarifs.sub.pendingPacks', { count: currentSub.pending_quantity ?? 2 })
                      : '',
                  date: new Date(currentSub.pending_effective_date).toLocaleDateString(
                    i18n.language,
                    {
                      day: '2-digit',
                      month: 'long',
                      year: 'numeric',
                    }
                  ),
                })}
              </div>
            </div>
          )}
        </div>
      ) : (
        /* No subscription */
        <div className="flex items-center gap-3 p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700">
          <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
          <p className="text-sm text-amber-800 dark:text-amber-300">
            {t('tarifs.sub.noSubscription')}
          </p>
        </div>
      )}

      {/* Plans grid + cart */}
      {!loading && (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 items-start">
          {/* Plans grid */}
          <div>
            <h2 className="text-base font-semibold text-gray-700 dark:text-gray-300 mb-4">
              {currentSub ? t('tarifs.plans.changePlan') : t('tarifs.plans.choosePlan')}
            </h2>
            <div
              className={`grid grid-cols-1 sm:grid-cols-2 ${plans.length >= 4 ? 'xl:grid-cols-4' : 'xl:grid-cols-3'} gap-5`}
            >
              {plans.map((plan) => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  currentPlan={currentSub?.plan_name ?? null}
                  selected={cartEntry?.plan.id === plan.id}
                  onSelect={() => !plan.is_custom && handleSelectPlan(plan)}
                />
              ))}
            </div>

            {plans.length === 0 && (
              <div className="text-center py-16 text-gray-400">
                <CalendarCheck className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p>{t('tarifs.plans.noPlans')}</p>
              </div>
            )}

            {/* FAQ / info */}
            <div className="mt-8 p-5 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 text-sm text-blue-800 dark:text-blue-300 space-y-1">
              <p className="font-semibold">{t('tarifs.plans.howTitle')}</p>
              <p>{t('tarifs.plans.howDesc1')}</p>
              <p>{t('tarifs.plans.howDesc2')}</p>
            </div>
          </div>

          {/* Cart sidebar */}
          <CartPanel
            entry={cartEntry}
            onQtyChange={(qty) =>
              setCartEntry((prev) => (prev ? { ...prev, quantity: qty } : null))
            }
            onRemove={() => {
              setCartEntry(null)
              setOrderMsg(null)
              setPaymentStep(null)
            }}
            onOrder={handleOrder}
            ordering={ordering}
            success={orderMsg?.type === 'success' ? orderMsg.text : null}
            error={orderMsg?.type === 'error' ? orderMsg.text : null}
            paymentStep={paymentStep}
            onPaymentSuccess={handlePaymentSuccess}
            onCancelPayment={handleCancelPayment}
          />
        </div>
      )}
    </div>
  )
}
